import PropTypes from 'prop-types';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import BackButton from 'src/components/BackButton';
import DeleteButton from 'src/components/DeleteButton';
import Iconify from 'src/components/Iconify';
import WarningAlert from 'src/components/WarningAlert';
import { useDeleteDeploymentRing } from 'src/hooks/deploymentRing';
import { pagesText } from 'src/constants/pagesText';

// Only reachable for a non-default ring - the delete action on the default row is disabled before
// this dialog ever opens.
const DeleteDeploymentRingDialog = ({ ring, onCancel, onDeleted }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: deleteDeploymentRing, isLoading } = useDeleteDeploymentRing();
  const hasDevices = ring.deviceCount > 0;

  const handleConfirm = async () => {
    try {
      await deleteDeploymentRing({ ringId: ring.id });
      enqueueSnackbar('Deployment ring deactivated.', {
        variant: 'success',
        autoHideDuration: 3000,
      });
      onDeleted();
    } catch (error) {
      enqueueSnackbar(typeof error === 'string' && error ? error : pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { backgroundColor: 'background.dialog' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon="mdi:trash-can-outline" width={22} height={22} color="#DE312B" />
        Deactivate Deployment Ring
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#F3F4F6', fontWeight: 600, mb: 1.5 }}>
          {ring.name}
        </Typography>
        {hasDevices ? (
          <WarningAlert sx={{ py: 0.25 }}>
            <Typography variant="caption">
              {ring.deviceCount === 1
                ? '1 device is assigned to this ring.'
                : `${ring.deviceCount} devices are assigned to this ring.`}{' '}
              Reassign them to another ring before deactivating it.
            </Typography>
          </WarningAlert>
        ) : (
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            The ring will stay listed as inactive.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BackButton disabled={isLoading} onClick={onCancel}>
          Cancel
        </BackButton>
        <DeleteButton disabled={isLoading || hasDevices} onClick={handleConfirm}>
          Deactivate
        </DeleteButton>
      </DialogActions>
    </Dialog>
  );
};

DeleteDeploymentRingDialog.propTypes = {
  ring: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    deviceCount: PropTypes.number,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};

export default DeleteDeploymentRingDialog;
